/**
 * Recursively parses an XML string into a JavaScript/TypeScript object.
 *
 * @param xml - The XML string to parse.
 * @param arrayTags - An optional array of tag names that should always be treated as arrays,
 *                    even if they only contain a single child.
 * @returns A structured object representing the XML.
 */
export function parseXml(xml: string, arrayTags?: string[]): Record<string, any> {
    let dom: Document | null = null;

    if (typeof window !== 'undefined' && window.DOMParser) {
        dom = new DOMParser().parseFromString(xml, 'text/xml');
    } else if (typeof (window as any)?.ActiveXObject !== 'undefined') {
        // IE fallback
        dom = new (window as any).ActiveXObject('Microsoft.XMLDOM');
        if (dom) {
            (dom as any).async = false;
            if (!(dom as any).loadXML(xml)) {
                throw new Error(`${(dom as any).parseError.reason} ${(dom as any).parseError.srcText}`);
            }
        }
    } else {
        throw new Error('cannot parse xml string!');
    }

    if (!dom) throw new Error('DOM initialization failed');

    function parseNode(xmlNode: Node, result: Record<string, any>): void {
        // Handle text nodes
        if (xmlNode.nodeName === '#text') {
            const v = xmlNode.nodeValue;

            if (v && v.trim()) {
                result['#text'] = v;
            }

            return;
        }

        const jsonNode: Record<string, any> = {};
        const { nodeName } = xmlNode;
        const existing = result[nodeName];

        // Determine if this node should be an array
        if (existing) {
            if (!Array.isArray(existing)) {
                result[nodeName] = [existing, jsonNode];
            } else {
                existing.push(jsonNode);
            }
        } else if (arrayTags && arrayTags.indexOf(nodeName) !== -1) {
            result[nodeName] = [jsonNode];
        } else {
            result[nodeName] = jsonNode;
        }

        // Handle attributes
        if (xmlNode instanceof Element && xmlNode.attributes) {
            for (let i = 0; i < xmlNode.attributes.length; i++) {
                const attribute = xmlNode.attributes[i];

                jsonNode[attribute.nodeName] = attribute.nodeValue;
            }
        }

        // Recursive call for children
        if (xmlNode.childNodes) {
            for (let i = 0; i < xmlNode.childNodes.length; i++) {
                parseNode(xmlNode.childNodes[i], jsonNode);
            }
        }
    }

    const result: Record<string, any> = {};

    // Iterate through root nodes
    for (let i = 0; i < dom.childNodes.length; i++) {
        parseNode(dom.childNodes[i], result);
    }

    return result;
}
